import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, DownloadSimple, Plus, QrCode } from '@phosphor-icons/react'
import {
  Banner,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  DataTable,
  EmptyState,
  Field,
  IntegerInput,
  Select,
  type Column,
} from '@/ui'
import {
  fetchQrBatch,
  useGenerateQrBatch,
  useQrBatches,
  useQrVillagePreview,
  type GateQrKind,
  type QrBatch,
  type QrCodeRecord,
} from '../api/qrGeneration'
import { useVillages } from '../api/reference'
import { errorMessage } from '../api/core'
import { ActionButton } from '../components/Can'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage } from '../components/Layout'
import { Code, Status, When } from '../components/bits'
import { downloadQrBatchPdf } from '../lib/qrPdf'
import { qrPayload, sequenceToSuffix } from '../model/qrGeneration'

const HISTORY_LIMIT = 20

/** Office workflow: generate, preview/print, then hand the physical roll to its village. */
export function QrGenerationPage() {
  const navigate = useNavigate()
  const villages = useVillages()
  const [groupId, setGroupId] = useState('')
  const [kind, setKind] = useState<GateQrKind>('BAG')
  const [count, setCount] = useState<number | null>(null)
  const [generated, setGenerated] = useState<QrBatch | null>(null)
  const [historyVillage, setHistoryVillage] = useState('')
  const [historyPage, setHistoryPage] = useState(1)
  const preview = useQrVillagePreview(groupId)
  const generate = useGenerateQrBatch()
  const history = useQrBatches({
    groupId: historyVillage || undefined,
    page: historyPage,
    limit: HISTORY_LIMIT,
  })
  const village = villages.data?.find((v) => v.id === groupId) ?? null
  const validCount = count !== null && count >= 1 && count <= 5000
  const start = preview.data?.nextSequence ?? null
  const end = start !== null && validCount ? start + count - 1 : null
  const firstPayload =
    start !== null && village?.stateCode
      ? qrPayload(village.stateCode, preview.data?.code ?? '', start, kind)
      : null
  const lastPayload =
    end !== null && village?.stateCode
      ? qrPayload(village.stateCode, preview.data?.code ?? '', end, kind)
      : null

  const villageOptions = useMemo(
    () =>
      (villages.data ?? []).map((v) => ({
        value: v.id,
        label: v.hint ? `${v.name} · ${v.hint}` : v.name,
      })),
    [villages.data],
  )

  const submit = async () => {
    if (!groupId || !validCount || count === null) return
    const result = await generate.mutateAsync({ groupId, count, kind })
    setGenerated(result)
  }

  const columns: Column<QrBatch>[] = [
    {
      id: 'kind',
      header: 'Kind',
      cell: (b) => <Status status={b.kind} label={b.kind === 'SEAL' ? 'Seal strip' : 'Bag tags'} />,
    },
    { id: 'village', header: 'Village', cell: (b) => b.villageName },
    {
      id: 'range',
      header: 'Printed range',
      cell: (b) => (
        <Code>
          {batchPayload(b, b.startSequence)} – {batchPayload(b, b.endSequence)}
        </Code>
      ),
    },
    { id: 'count', header: 'Codes', align: 'right', mono: true, cell: (b) => b.count },
    { id: 'created', header: 'Generated', cell: (b) => <When at={b.createdAt} />, hideBelow: 'md' },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      cell: (b) => <PdfButton batch={b} compact />,
    },
  ]

  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Generate rolls & seals"
      description="Create permanently unique bag-tag rolls or seal strips, download the printable PDF, then give the physical batch to its village from Rolls & tags."
      back={{ to: '/c/rolls', label: 'Rolls & tags' }}
    >
      <Card>
        <CardHeader
          eyebrow="New batch"
          title="Choose what will be printed"
          description="The server owns the sequence counter. Start and end numbers are assigned automatically and can never overlap an older batch."
        />
        <CardBody className="grid gap-4 md:grid-cols-3">
          <Field label="Village" required>
            <Select
              value={groupId}
              onChange={(value) => {
                setGroupId(value)
                setGenerated(null)
              }}
              placeholder={villages.isLoading ? 'Loading villages…' : 'Select village'}
              options={villageOptions}
              disabled={villages.isLoading}
            />
          </Field>
          <Field label="Print as" required hint="Bag tags and sample seals are separate ranges.">
            <Select<GateQrKind>
              value={kind}
              onChange={(value) => {
                setKind(value)
                setGenerated(null)
              }}
              options={[
                { value: 'BAG', label: 'Bag-tag roll' },
                { value: 'SEAL', label: 'Sample seal strip' },
              ]}
            />
          </Field>
          <Field
            label="Number of codes"
            required
            hint="1–5,000 codes in one batch"
            error={count !== null && !validCount ? 'Enter a number from 1 to 5,000.' : undefined}
          >
            <IntegerInput
              value={count}
              onChange={(value) => {
                setCount(value)
                setGenerated(null)
              }}
              maxDigits={4}
              placeholder="e.g. 250"
            />
          </Field>
        </CardBody>

        {preview.isError && (
          <div className="px-4 pb-4">
            <Banner tone="error" title="This village is not ready for QR generation">
              {errorMessage(preview.error)}
            </Banner>
          </div>
        )}

        {groupId && preview.data && validCount && (
          <div className="mx-4 mb-4 grid gap-3 rounded-card border border-rule bg-paper p-4 sm:grid-cols-2 lg:grid-cols-4">
            <PreviewFact label="Village">{village?.name ?? '—'}</PreviewFact>
            <PreviewFact label="Type">{kind === 'SEAL' ? 'Sample seals' : 'Bag tags'}</PreviewFact>
            <PreviewFact label="Start">
              <Code>{firstPayload ?? start}</Code>
            </PreviewFact>
            <PreviewFact label="End">
              <Code>{lastPayload ?? end}</Code>
            </PreviewFact>
          </div>
        )}

        <CardFooter>
          <ActionButton
            perm="qr-generation:create"
            leadingIcon={<Plus aria-hidden="true" />}
            loading={generate.isPending}
            disabled={!groupId || !validCount || preview.isLoading || preview.isError}
            onClick={() => void submit()}
          >
            Generate {kind === 'SEAL' ? 'seal strip' : 'tag roll'}
          </ActionButton>
        </CardFooter>
      </Card>

      {generated && (
        <Card>
          <CardHeader
            eyebrow="Ready to print"
            title={`${generated.count} ${generated.kind === 'SEAL' ? 'seals' : 'bag tags'} generated for ${generated.villageName}`}
            description={`${batchPayload(generated, generated.startSequence)} – ${batchPayload(generated, generated.endSequence)}`}
            actions={<PdfButton batch={generated} />}
          />
          <CardBody className="flex flex-col gap-4">
            <QrPreviewGrid codes={generated.codes} />
            {generated.codes.length > 24 && (
              <p className="text-[13px] text-muted">
                Showing the first 24 of {generated.codes.length}. The downloaded PDF contains every
                code.
              </p>
            )}
            <Banner tone="info" title="Print first, then issue the physical roll">
              The phone accepts these codes only after the batch is given to the village from Rolls
              & tags.
            </Banner>
          </CardBody>
          <CardFooter>
            <Button
              variant="secondary"
              trailingIcon={<ArrowRight aria-hidden="true" />}
              onClick={() =>
                navigate(`/c/rolls?groupId=${encodeURIComponent(generated.villageId ?? '')}`)
              }
            >
              Give to village
            </Button>
          </CardFooter>
        </Card>
      )}

      <DataTable<QrBatch>
        caption="Generation history"
        columns={columns}
        rows={history.data?.rows ?? []}
        getRowId={(b) => b.id}
        loading={history.isLoading}
        error={history.isError ? errorMessage(history.error) : undefined}
        pageSize={HISTORY_LIMIT}
        server={{
          page: historyPage,
          total: history.data?.total ?? 0,
          onPageChange: setHistoryPage,
        }}
        filters={
          <FilterBar
            active={Boolean(historyVillage)}
            onClear={() => {
              setHistoryVillage('')
              setHistoryPage(1)
            }}
          >
            <SelectFilter
              label="Village"
              value={historyVillage}
              onChange={(value) => {
                setHistoryVillage(value)
                setHistoryPage(1)
              }}
              options={villageOptions}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<QrCode />}
            title="No generated batches"
            description="Generate the first bag-tag roll or seal strip above."
          />
        }
      />
    </ConsolePage>
  )
}

function PreviewFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-mono text-[10px] font-bold uppercase tracking-label text-muted">
        {label}
      </div>
      <div className="mt-1 text-[14px] font-semibold text-ink">{children}</div>
    </div>
  )
}

/**
 * The code exactly as printed when the batch carries its codes. The list has no codes, and older
 * batches were printed with S instead of B/L, so it shows only the running number there.
 */
function batchPayload(batch: QrBatch, sequence: number): string {
  const printed = batch.codes.find((c) => c.sequence === sequence)?.payload
  if (printed) return printed
  return `${batch.villageCode || '—'} ${sequenceToSuffix(sequence)}`
}

function PdfButton({ batch, compact = false }: { batch: QrBatch; compact?: boolean }) {
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState('')
  const [error, setError] = useState<string | null>(null)
  const download = async () => {
    setLoading(true)
    setError(null)
    try {
      const detail = batch.codes.length ? batch : await fetchQrBatch(batch.id)
      await downloadQrBatchPdf(detail, (done, total) => setProgress(`${done}/${total}`))
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setLoading(false)
      setProgress('')
    }
  }
  return (
    <div className="flex flex-col items-end gap-1">
      <ActionButton
        perm="qr-generation:read"
        size={compact ? 'sm' : 'md'}
        variant={compact ? 'ghost' : 'secondary'}
        leadingIcon={<DownloadSimple aria-hidden="true" />}
        loading={loading}
        onClick={() => void download()}
      >
        {loading && progress ? progress : compact ? 'PDF' : 'Download PDF'}
      </ActionButton>
      {error && <span className="max-w-56 text-right text-[11px] text-danger">{error}</span>}
    </div>
  )
}

function QrPreviewGrid({ codes }: { codes: QrCodeRecord[] }) {
  const shown = useMemo(() => codes.slice(0, 24), [codes])
  const [images, setImages] = useState<Record<string, string>>({})
  useEffect(() => {
    let live = true
    void import('qrcode').then(async ({ default: QRCode }) => {
      const pairs = await Promise.all(
        shown.map(
          async (code) =>
            [
              code.payload,
              await QRCode.toDataURL(code.payload, {
                width: 180,
                margin: 1,
                errorCorrectionLevel: 'M',
              }),
            ] as const,
        ),
      )
      if (live) setImages(Object.fromEntries(pairs))
    })
    return () => {
      live = false
    }
  }, [shown])

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      {shown.map((code) => (
        <div
          key={code.id}
          className="flex flex-col items-center gap-2 rounded-card border border-rule p-2"
        >
          {images[code.payload] ? (
            <img
              src={images[code.payload]}
              alt={code.payload}
              className="aspect-square w-full max-w-36"
            />
          ) : (
            <div className="aspect-square w-full max-w-36 animate-pulse bg-rule/40" />
          )}
          <Code className="break-all text-center text-[10px]">{code.payload}</Code>
        </div>
      ))}
    </div>
  )
}
